/*
 * <lookbook-gallery> — front-image grid + full-screen layer popup (Swiper).
 *
 * Layout/styling lives in piner.css (.lb-layer-popup, .lb-lp-*, .swiper-*,
 * body.scroll-lock). The slider is a real Swiper instance (swiper-bundle,
 * loaded before this file). Behavior:
 *   - click a grid item → open the popup on that look, image 1
 *   - swipe / left-right half clicks / arrow keys → prev/next IMAGE
 *   - hover left/right half → reveal < / > indicators over the image
 *     (first image shows only >, last only <)
 *   - Previous / Next (popup edges) → previous / next LOOK card
 *   - the swiper box takes the first image's natural aspect ratio, so the
 *     Look name (bottom-left) and counter (bottom-right) pin to the image
 *   - Close (.lb-lp-close-btn) or ESC → close, unlock scroll
 *   - mobile: "Season" toggle opens the season list + dim (.season-open)
 */
class LookbookGallery extends HTMLElement {
  // How wide the popup image actually renders — lets srcset pick a
  // right-sized file instead of always downloading the biggest one.
  static POPUP_SIZES = '(max-width: 768px) 92vw, 600px';

  connectedCallback() {
    // Season selector (mobile): toggle the list + dim via .season-open on this element.
    this.querySelectorAll('[data-season-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = this.classList.toggle('season-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
    this.querySelectorAll('[data-lb-nav-dim]').forEach((dim) => {
      dim.addEventListener('click', () => this.#closeSeasons());
    });
    // Outside click closes the season list — mirrors the header gnb behavior
    // and keeps the two dropdowns mutually exclusive (opening Explore closes
    // Season, and vice versa via the header's own outside-click handler).
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.lb-nav-seasons')) this.#closeSeasons();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#closeSeasons();
    });

    // Blur-up: fade grid thumbnails in as they finish loading.
    this.querySelectorAll('.lb-blur img').forEach((img) => this.#fadeIn(img));

    this.looks = this.#parseData();
    if (!this.looks.length) return;

    this.popup = this.querySelector('[data-lb-popup]');
    this.swiperEl = this.querySelector('[data-lb-swiper]');
    this.track = this.querySelector('[data-lb-track]');
    this.counterEl = this.querySelector('[data-lb-counter]');
    this.nameEl = this.querySelector('[data-lb-lookname]');
    this.imgPrev = this.querySelector('[data-lb-img-prev]');
    this.imgNext = this.querySelector('[data-lb-img-next]');

    this.swiper = null;
    this.lookIndex = 0;
    this.isOpen = false;
    this.hoverSide = null;

    // Open triggers (grid items — plain divs with role="button").
    // Hovering / touching a card warms up its popup images so they're already
    // cached by the time the popup opens.
    this.querySelectorAll('[data-lb-open]').forEach((el) => {
      const index = parseInt(el.dataset.lookIndex, 10) || 0;
      const openLook = () => this.open(index);
      const warmLook = () => this.#preloadLook(index);
      el.addEventListener('click', openLook);
      el.addEventListener('mouseenter', warmLook);
      el.addEventListener('touchstart', warmLook, { passive: true });
      el.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openLook();
        }
      });
    });

    // Close buttons (.lb-lp-close-btn and any other [data-lb-close])
    this.querySelectorAll('[data-lb-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    // Popup edges: previous / next LOOK card (no wrap — disabled at the ends)
    this.lookPrevBtns = Array.from(this.querySelectorAll('[data-lb-prev-look]'));
    this.lookNextBtns = Array.from(this.querySelectorAll('[data-lb-next-look]'));
    this.lookPrevBtns.forEach((el) => {
      el.addEventListener('click', () => this.#changeLook(-1));
    });
    this.lookNextBtns.forEach((el) => {
      el.addEventListener('click', () => this.#changeLook(1));
    });

    // Hover reveals the < / > indicators on the matching image half
    this.swiperEl?.addEventListener('mousemove', (event) => {
      const rect = this.swiperEl.getBoundingClientRect();
      this.#setHover(event.clientX - rect.left < rect.width / 2 ? 'left' : 'right');
    });
    this.swiperEl?.addEventListener('mouseleave', () => this.#setHover(null));

    document.addEventListener('keydown', (event) => {
      if (this.isOpen && event.key === 'Escape') this.close();
    });
  }

  #closeSeasons() {
    this.classList.remove('season-open');
    this.querySelectorAll('[data-season-toggle]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
  }

  #parseData() {
    const node = this.querySelector('[data-lb-data]');
    if (!node) return [];
    try {
      // Keep every look so indices stay aligned with the grid items.
      return JSON.parse(node.textContent).map((look) => ({ name: look.name, images: look.images || [] }));
    } catch (error) {
      console.warn('[lookbook] look data JSON parse failed — popup disabled', error);
      return [];
    }
  }

  open(lookIndex) {
    const look = this.looks[lookIndex];
    if (!look || !look.images.length) {
      console.warn('[lookbook] look', lookIndex, 'has no popup images — check the metaobject image field');
      return;
    }

    this.lookIndex = lookIndex;
    // Open on the first image (front) — 1/3.
    this.startIndex = 0;
    // All of the look's images load right away (there are only a few) so
    // swiping never waits; the starting slide gets network priority.
    this.track.innerHTML = look.images
      .map((img, i) => {
        const alt = (img.alt || '').replace(/"/g, '&quot;');
        const srcset = img.srcset ? ` srcset="${img.srcset}" sizes="${LookbookGallery.POPUP_SIZES}"` : '';
        const priority = i === this.startIndex ? ' fetchpriority="high"' : '';
        // Blur-up: tiny version behind, real image fades in on load. The
        // aspect-ratio keeps the blur box correctly sized before load.
        const ratio = img.w && img.h ? `; aspect-ratio: ${img.w} / ${img.h}` : '';
        const blurStyle = `--blur-bg: url('${img.tiny || img.src}')${ratio}`;
        return `<div class="swiper-slide"><span class="lb-blur" style="${blurStyle}"><img src="${img.src}"${srcset}${priority} alt="${alt}" decoding="async" draggable="false"></span></div>`;
      })
      .join('');
    this.track.querySelectorAll('img').forEach((img) => this.#fadeIn(img));

    if (this.nameEl) this.nameEl.textContent = look.name;

    this.popup.hidden = false;
    document.body.classList.add('scroll-lock');
    this.isOpen = true;

    this.#matchAspectRatio();
    this.#initOrUpdateSwiper();
    this.#updateLookNav();

    // Warm up the adjacent looks so Previous / Next feel instant.
    this.#preloadLook(this.#nextLookIndex(1));
    this.#preloadLook(this.#nextLookIndex(-1));
  }

  close() {
    if (!this.isOpen) return;
    this.popup.hidden = true;
    document.body.classList.remove('scroll-lock');
    this.isOpen = false;
    this.#setHover(null);
    if (this.swiper) this.swiper.keyboard.disable();
  }

  // Size the swiper box to the image's natural ratio so the box (and the
  // overlays pinned to its corners) hugs the image exactly. Dimensions come
  // from the look data when available (instant), else from the loaded image.
  #matchAspectRatio() {
    const data = this.looks[this.lookIndex].images[this.startIndex];
    if (data && data.w && data.h) {
      this.swiperEl.style.aspectRatio = `${data.w} / ${data.h}`;
      if (this.swiper) this.swiper.update();
      return;
    }

    const firstImage = this.track.querySelector('img');
    if (!firstImage) return;

    const apply = () => {
      if (!firstImage.naturalWidth || !firstImage.naturalHeight) return;
      this.swiperEl.style.aspectRatio = `${firstImage.naturalWidth} / ${firstImage.naturalHeight}`;
      if (this.swiper) this.swiper.update();
    };

    if (firstImage.complete) apply();
    else firstImage.addEventListener('load', apply, { once: true });
  }

  // Blur-up: reveal the real image once it has loaded.
  #fadeIn(img) {
    const done = () => img.classList.add('is-loaded');
    if (img.complete && img.naturalWidth) done();
    else img.addEventListener('load', done, { once: true });
  }

  // Fetch a look's images into the browser cache without rendering them.
  #preloadLook(index) {
    const look = this.looks[index];
    if (!look || look.warmed) return;
    look.warmed = true;
    look.images.forEach((img) => {
      const el = new Image();
      if (img.srcset) {
        el.sizes = LookbookGallery.POPUP_SIZES;
        el.srcset = img.srcset;
      }
      el.src = img.src;
    });
  }

  // Nearest look with images in the given direction, without wrapping.
  // Returns -1 when there is none (we're at that end).
  #nextLookIndex(direction) {
    for (let index = this.lookIndex + direction; index >= 0 && index < this.looks.length; index += direction) {
      if (this.looks[index].images.length) return index;
    }
    return -1;
  }

  #changeLook(direction) {
    const index = this.#nextLookIndex(direction);
    if (index !== -1) this.open(index);
  }

  #updateLookNav() {
    const noPrev = this.#nextLookIndex(-1) === -1;
    const noNext = this.#nextLookIndex(1) === -1;
    this.lookPrevBtns?.forEach((el) => (el.disabled = noPrev));
    this.lookNextBtns?.forEach((el) => (el.disabled = noNext));
  }

  #initOrUpdateSwiper() {
    if (typeof Swiper === 'undefined') return;

    if (!this.swiper) {
      this.swiper = new Swiper(this.swiperEl, {
        slidesPerView: 1,
        speed: 350,
        initialSlide: this.startIndex,
        keyboard: { enabled: true },
        on: {
          slideChange: () => this.#updateUI(),
          // Click the left/right half of the image to navigate (skipped after drags).
          click: (swiper, event) => {
            if (!swiper.allowClick || !(event instanceof MouseEvent)) return;
            const rect = this.swiperEl.getBoundingClientRect();
            if (event.clientX - rect.left < rect.width / 2) swiper.slidePrev();
            else swiper.slideNext();
          },
        },
      });
      // afterInit fires inside the constructor — before this.swiper is
      // assigned — so the first counter update must happen here instead.
      this.#updateUI();
    } else {
      this.swiper.update();
      this.swiper.slideTo(this.startIndex, 0);
      this.swiper.keyboard.enable();
      this.#updateUI();
    }
  }

  #updateUI() {
    if (!this.swiper) return;
    const total = this.looks[this.lookIndex].images.length;
    this.counterEl.textContent = `${this.swiper.activeIndex + 1}/${total}`;
    // Re-evaluate hover indicators against the new first/last state.
    this.#setHover(this.hoverSide);
  }

  #setHover(side) {
    this.hoverSide = side;
    const canPrev = this.swiper ? !this.swiper.isBeginning : false;
    const canNext = this.swiper ? !this.swiper.isEnd : true;
    this.imgPrev?.classList.toggle('is-visible', side === 'left' && canPrev);
    this.imgNext?.classList.toggle('is-visible', side === 'right' && canNext);
  }
}

if (!customElements.get('lookbook-gallery')) {
  customElements.define('lookbook-gallery', LookbookGallery);
}
